// The widget types a merchandiser can author in the CMS. Keep in sync with
// apps/api/routes/merchandising/models.py WIDGET_TYPES and the
// widget.widget_type CHECK (migrations 49, 57, 67, 76, 79, 85, 102, 103, 107,
// 116, 118).
//
// Card carousels are four first-class widget types (migration 85 promoted them
// from the old coarse `carousel` marker + metadata.carousel_type). The
// size/orientation variant now lives in the widget_type itself; each authors
// with the same card-rail config (see CarouselEditor).
//
// 'hero_banner' is its own widget_type since migration 79; its slides are
// authored in HeroBannerEditor and stored in metadata.slides.
//
// 'notification_banner' (migration 102) is an alert strip for match-delay /
// programming-change notices. Its payload matches editorial (a markdown body)
// but the type is what tells clients to render an alert strip — see
// docs/html/tennischannel/widgets/notification-banner.html.
// 'faq' (migration 103) is an expandable Q/A accordion. Its items are
// first-class metadata fields (question plain text, answer markdown) rather
// than an editorial markdown convention, so authors can't break the accordion
// structure — see docs/html/tennischannel/widgets/faq.html.
// 'standard_landscape_grid' (migration 107) authors with the same card-rail
// config as the carousels but renders as a wrapping grid instead of a rail.
// It is deliberately NOT in CAROUSEL_WIDGET_TYPES — it's a standalone picker
// entry with its own editor, not a fifth size/orientation variant — see
// docs/html/tennischannel/widgets/content-grid.html.
// 'plan_selection' (migration 116) is the Monthly/Annual plan picker. The
// author picks offer codes and writes the copy framing them; every number —
// price, discount, discounted price — resolves server-side from the offer
// catalog and the live coupon, so no price is authorable here. Copy comes in
// two layers: base strings plus a `promo` block that overrides them while a
// promotion runs on that offer — see
// docs/html/tennischannel/widgets/plan-selection.html.
// 'tournament_carousel' (migration 118) is a rail of the tournaments that run
// in the next seven days. The title is the full metadata — the selection rule
// is a constant in ACE, so there is nothing else to author — see
// docs/html/tennischannel/widgets/tournament-carousel.html. Not a
// CAROUSEL_WIDGET_TYPES member: it carries no card-rail config.

// Which widget types are valid on which site. The two product lines have
// mostly-disjoint catalogues (docs/html/tennischannel/widget-inventory.html):
// tennis.com gets the four editorial/web widgets; Tennis Channel gets the
// carousel and hero banner. `sites` holds site.code values
// (shared/migrations/49_merchandising.sql). editorial is the one shared type —
// Decision F resolved to share it (markdown prose isn't surface-specific), so
// it authors on both sites.
export const WIDGET_TYPES = [
  { code: 'video_embed', label: 'Video Embed', sites: ['tennis_com'] },
  { code: 'editorial', label: 'Editorial', sites: ['tennis_com', 'tennis_channel'] },
  { code: 'banner_image', label: 'Banner Image', sites: ['tennis_com'] },
  { code: 'google_ad_slot', label: 'Google Ad Slot', sites: ['tennis_com'] },
  { code: 'standard_landscape_carousel', label: 'Standard Landscape Carousel', sites: ['tennis_channel'] },
  { code: 'standard_portrait_carousel', label: 'Standard Portrait Carousel', sites: ['tennis_channel'] },
  { code: 'small_landscape_carousel', label: 'Small Landscape Carousel', sites: ['tennis_channel'] },
  { code: 'large_landscape_carousel', label: 'Large Landscape Carousel', sites: ['tennis_channel'] },
  { code: 'hero_banner', label: 'Hero Banner', sites: ['tennis_channel'] },
  { code: 'notification_banner', label: 'Notification Banner', sites: ['tennis_channel'] },
  { code: 'faq', label: 'FAQ', sites: ['tennis_channel'] },
  { code: 'standard_landscape_grid', label: 'Standard Landscape Grid', sites: ['tennis_channel'] },
  { code: 'plan_selection', label: 'Plan Selection', sites: ['tennis_channel'] },
  { code: 'tournament_carousel', label: 'Tournament Carousel', sites: ['tennis_channel'] },
] as const

export type WidgetTypeCode = typeof WIDGET_TYPES[number]['code']

// The card-carousel widget types. All four share the card-rail authoring
// config (CarouselEditor); the widget_type alone carries the card size and
// orientation. Keep in sync with apps/api/routes/merchandising/models.py
// CAROUSEL_WIDGET_TYPES.
export const CAROUSEL_WIDGET_TYPES = [
  'standard_landscape_carousel',
  'standard_portrait_carousel',
  'small_landscape_carousel',
  'large_landscape_carousel',
] as const

export type CarouselWidgetTypeCode = typeof CAROUSEL_WIDGET_TYPES[number]

export const isCarouselWidgetType = (code: string): code is CarouselWidgetTypeCode =>
  (CAROUSEL_WIDGET_TYPES as readonly string[]).includes(code)

// The one carousel variant that renders portrait artwork; the other three are
// 16:9 (docs/html/tennischannel/widget-inventory.html). Editors ask this to
// preview a card at the crop the rail will actually use — tc-web cuts the
// portrait tile 3:4 (CardTile.module.scss).
export const isPortraitCarouselWidgetType = (code: string): boolean =>
  code === 'standard_portrait_carousel'

// Widget types whose public row title is authored in the WidgetForm header
// (the Name/Title pair with name↔title mirroring) rather than inside their
// metadata editor: the card rails and the card grid, whose shared config form
// (CardCarouselEditor) carries no title field of its own, and the tournament
// rail, whose title is its entire metadata. editorial / video_embed / faq
// author their titles inside their own editors instead.
export const widgetTypeHasFormTitle = (code: string): boolean =>
  isCarouselWidgetType(code) || code === 'standard_landscape_grid' || code === 'tournament_carousel'

// Short display label for a widget-type badge (placements list, widget library,
// placement detail). The four carousel variants collapse to a single "Carousel"
// — the size/orientation distinction is a presentational sub-choice made in the
// carousel edit form, and is just noise on a badge. The grid shortens to "Grid"
// for the same reason.
export const getWidgetTypeLabel = (code: string): string => {
  if (isCarouselWidgetType(code)) return 'Carousel'
  if (code === 'standard_landscape_grid') return 'Grid'
  if (code === 'tournament_carousel') return 'Tournaments'
  const found = WIDGET_TYPES.find(wt => wt.code === code)
  return found ? found.label : code
}

// The color family a widget type is shown in. Every place that names a widget's
// type — the badge, the library's type filters, the type picker — takes its
// color from here, so one type reads the same throughout. The four carousel
// variants share the 'carousel' family for the same reason getWidgetTypeLabel
// collapses them: to a merchandiser they are one kind of widget.
//
// Keep the keys in sync with $tc-widget-type-colors in src/styles/globals.scss,
// which holds the colors themselves. Typed as a complete record of
// WidgetTypeCode so a type added to WIDGET_TYPES without a color fails to
// compile rather than rendering an uncolored badge.
export type WidgetTypeColorKey =
  | 'carousel'
  | 'grid'
  | 'hero'
  | 'faq'
  | 'notification'
  | 'tournament'
  | 'video'
  | 'plan'
  | 'editorial'
  | 'banner'
  | 'ad'

const WIDGET_TYPE_COLOR_KEYS: Record<WidgetTypeCode, WidgetTypeColorKey> = {
  video_embed: 'video',
  editorial: 'editorial',
  banner_image: 'banner',
  google_ad_slot: 'ad',
  standard_landscape_carousel: 'carousel',
  standard_portrait_carousel: 'carousel',
  small_landscape_carousel: 'carousel',
  large_landscape_carousel: 'carousel',
  hero_banner: 'hero',
  notification_banner: 'notification',
  faq: 'faq',
  standard_landscape_grid: 'grid',
  plan_selection: 'plan',
  tournament_carousel: 'tournament',
}

// `code` is a plain string at the call sites (it comes from stored widget rows),
// so an unrecognised value has to resolve to no family — its badge then falls
// back to the neutral gray tint.
export const getWidgetTypeColorKey = (code: string): WidgetTypeColorKey | null =>
  WIDGET_TYPE_COLOR_KEYS[code as WidgetTypeCode] ?? null

// The widget types a merchandiser can author for the given site. An unknown or
// empty site code yields an empty list (nothing to author until a site is picked).
export const widgetTypesForSite = (siteCode: string) =>
  WIDGET_TYPES.filter(wt => (wt.sites as readonly string[]).includes(siteCode))

// The variant a freshly-created carousel starts as. All four share the same
// config, so the size/orientation is a safe default the merchandiser then
// adjusts via the selector in the carousel edit form (see CarouselEditor).
export const DEFAULT_CAROUSEL_WIDGET_TYPE: CarouselWidgetTypeCode = 'standard_landscape_carousel'

// Options for the carousel "Size & orientation" selector inside the edit form.
// Labels are the WIDGET_TYPES labels minus the redundant "Carousel" suffix (the
// selector already sits under a Carousel widget). WIDGET_TYPES stays the single
// source of truth for the label text.
export const CAROUSEL_SIZE_OPTIONS: ReadonlyArray<{ code: CarouselWidgetTypeCode; label: string }> =
  CAROUSEL_WIDGET_TYPES.map(code => ({
    code,
    label: (WIDGET_TYPES.find(wt => wt.code === code)?.label ?? code).replace(/ Carousel$/, ''),
  }))

// The size the type badge hides: a carousel's variant (as the edit form's
// selector names it), or a hero banner's metadata.size (unset renders LARGE).
export const getWidgetSizeLabel = (code: string, metadata: Record<string, unknown> | null | undefined): string | null => {
  if (isCarouselWidgetType(code)) return CAROUSEL_SIZE_OPTIONS.find(o => o.code === code)?.label ?? null
  if (code === 'hero_banner') return metadata?.size === 'STANDARD' ? 'Standard' : 'Large'
  return null
}

// Options for the widget-type picker at creation. The four carousel variants
// collapse into one "Carousel" entry; selecting it creates a
// DEFAULT_CAROUSEL_WIDGET_TYPE widget whose size is then chosen in the edit
// form. Every other type maps 1:1 to its WIDGET_TYPES entry.
export const widgetTypeOptionsForSite = (siteCode: string): Array<{ code: string; label: string }> => {
  const forSite = widgetTypesForSite(siteCode)
  const options: Array<{ code: string; label: string }> = forSite
    .filter(wt => !isCarouselWidgetType(wt.code))
    .map(wt => ({ code: wt.code, label: wt.label }))
  if (forSite.some(wt => isCarouselWidgetType(wt.code))) {
    options.push({ code: DEFAULT_CAROUSEL_WIDGET_TYPE, label: 'Carousel' })
  }
  return options
}
